import { Box, Slider, Typography } from '@mui/material'
import { Port } from '../../types/models'
import { PortNumber, BatteryOptimizerConfig, BatteryOptimizerPort, PortConfigBatteryOptimizer } from '../../types/ports'
import { DeviceControl } from '../DeviceControl'
import { PortControl } from './PortControl'
import { Trans } from 'react-i18next'
import { Text } from '../../components/atoms/Text'
import { BatteryTuning } from '../../components/BatteryTuning'
import { MAX_POWER_W, PENALTY_FIELDS, PenaltyField, floorToPowerUnit } from '../../utils/batopt-tuning'

/** Lower end of the self-use power sliders. */
const SELF_USE_MIN_POWER_W = 1000

/**
 * Controller for battery optimizer (port 0x220).
 *
 * The conf is always flashed in the v2 layout (`new_conf=1`): power limits in
 * 16 W units (no 65535 W ceiling any more), `max_soc` hard-coded to 100, and
 * the four algorithm fine-tuning penalties. `normalizeFlashConfig()` applies
 * the same rules the server applies to the pending config, so what the device
 * reads back equals what was sent.
 */
export class BatteryOptimizerPortControl extends PortControl<BatteryOptimizerPort, BatteryOptimizerConfig> {

  port: BatteryOptimizerPort

  constructor(deviceControl: DeviceControl, port: Port) {
    super(deviceControl, port)
    this.port = port as BatteryOptimizerPort
  }

  getDescription(): string { return this.app.t('Battery Optimizer') }

  getTypeName(): string { return this.app.t('PortType-BatteryOptimizer') }

  get group(): string { return this.app.t('PortGroup-Optimization') }

  /**
   * `new_conf` selects the wire layout, it is not a user setting: the server
   * always flashes the v2 layout, but a device last flashed in the legacy
   * layout reads back `new_conf: false` plus the dead legacy fields until its
   * conf is rewritten. Neither is an inconsistency the user can fix, so keep
   * them out of the hardware-vs-settings comparison.
   */
  get diffIgnoredKeys(): string[] {
    return ['new_conf', 'n_charge_hours', 'charge_hour_soc', 'n_self_use_hours', 'n_sell_hours', 'charge_hour_power']
  }

  getConfigDefaults(): PortConfigBatteryOptimizer {
    return {
      enabled: false,
      new_conf: true,
      time_constant: 24,
      battery_port: '22/0' as PortNumber,
      battery_dest: 0,
      battery_dest_kind: 'DEST_KIND_THIS_DEVICE',
      max_charge_power: 10000,
      max_discharge_power: 10000,
      max_soc: 100,
      min_soc: 12,
      penalty_pbs: 0,
      penalty_pb: 0,
      penalty_buy: 0,
      penalty_sell: 0,
    }
  }

  normalizeFlashConfig(config: Record<string, unknown>): Record<string, unknown> {
    const normalized: Record<string, unknown> = {
      ...config,
      new_conf: true,
      max_soc: 100,
      max_charge_power: floorToPowerUnit(Number(config.max_charge_power) || 0),
      max_discharge_power: floorToPowerUnit(Number(config.max_discharge_power) || 0),
    }
    for (const spec of PENALTY_FIELDS) {
      normalized[spec.field] = Number(config[spec.field]) || 0
    }
    return normalized
  }

  /** Current penalty values (wire units), missing ones as 0. */
  private penaltyValues(): Record<PenaltyField, number> {
    const values = {} as Record<PenaltyField, number>
    for (const spec of PENALTY_FIELDS) {
      values[spec.field] = Number(this.getConfig(spec.field)) || 0
    }
    return values
  }

  private resetPenalties(): void {
    for (const spec of PENALTY_FIELDS) {
      this.setConfig(spec.field, 0, false)
    }
    this.deviceControl.refresh()
  }

  /**
   * Self-use power limits as two sliders from 1 kW up to `maxPowerW` (the
   * inverter's nominal power), shown in kW at 0.1 kW resolution so the 16 W
   * wire rounding does not show (4992 W reads as 5.0 kW).
   */
  private powerSliders(maxPowerW: number): React.ReactNode {
    const fmt = (watts: number) => `${(watts / 1000).toFixed(1)} kW`
    const mark = (watts: number) => ({ value: watts, label: <Box sx={{ color: '#A3A3A3' }}>{fmt(watts)}</Box> })

    const slider = (key: 'max_charge_power' | 'max_discharge_power', label: string) =>
      <Box sx={{ textAlign: 'center', mt: 3 }}>
        <Typography>{this.app.t(label)}</Typography>
        <Slider
          data-cy={`slider-${label}`}
          size="medium"
          sx={{ mt: 5, width: '80%' }}
          value={Number(this.getConfig(key)) || 0}
          min={SELF_USE_MIN_POWER_W}
          max={maxPowerW}
          step={100}
          marks={[mark(SELF_USE_MIN_POWER_W), mark(maxPowerW)]}
          valueLabelFormat={fmt}
          valueLabelDisplay="on"
          onChange={(_, v) => this.setConfig(key, floorToPowerUnit(v as number))}
        />
      </Box>

    return <Box sx={{ mb: 3 }}>
      <Typography sx={{ fontWeight: 'bold', textAlign: 'center' }}><Trans>Self-use maximum power</Trans></Typography>
      {slider('max_charge_power', 'Charging Power, maximum')}
      {slider('max_discharge_power', 'Discharging Power, maximum')}
    </Box>
  }

  /**
   * @param socControls show the minimum SOC field
   * @param sliderMaxPowerW the inverter's nominal power when known (reserve
   *   batteries): power limits are then sliders up to it, text fields otherwise.
   * @param beforeTuning rendered directly above the algorithm fine-tuning
   *   (the reserve participation tuning of reserve batteries).
   */
  configBottom(socControls = true, sliderMaxPowerW: number | null = null, beforeTuning: React.ReactNode = null) : React.ReactNode | undefined {
    const maxChargePower = this.getConfig('max_charge_power')
    const maxDischargePower = this.getConfig('max_discharge_power')
    const minSoc = this.getConfig('min_soc')

    const enabledEnergyStorageInDevice = this.systemControl.ports({ portType: 'ENERGY_STORAGE' }).filter(port => port.device.hw_id === this.device.hw_id && port.getConfig('enabled'))

    const InfoForDoubleBattery = enabledEnergyStorageInDevice.length > 1
      ? <Box sx={{ my: 4 }}>
        <Trans>More than one battery installed. Give maximum powers as totals. Same minimum and maximum SOC% will be used for all batteries.</Trans>
      </Box>
      : <></>

    // Power limits travel in 16 W units: round down on blur so the value the
    // user sees is the value the device gets (and reads back).
    const setPower = (key: 'max_charge_power' | 'max_discharge_power', raw: string, floor: boolean) => {
      const value = parseInt(raw || '0')
      this.setConfig(key, floor ? floorToPowerUnit(value) : value)
    }

    const useSliders = !!sliderMaxPowerW && sliderMaxPowerW > SELF_USE_MIN_POWER_W

    return <Box>
      {InfoForDoubleBattery}
      {useSliders && this.powerSliders(sliderMaxPowerW)}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 7rem 2rem', rowGap: 1 }}>
        { !useSliders && <>
        <Box sx={{ textAlign: 'right', pt: 2, mr: 2, fontSize: '1.2rem' }}>
          <Trans>Charging Power, maximum</Trans>
        </Box>
        <Box>
          <Text
              data-cy="text-Charging Power, maximum"
              type="number"
              min={0}
              max={MAX_POWER_W}
              value={maxChargePower || 0}
              onChange={(e) => setPower('max_charge_power', e.target.value, false)}
              onBlur={(e) => setPower('max_charge_power', e.target.value, true)}
            />
        </Box>
        <Box sx={{ pt: 2, ml: 2, fontSize: '1.2rem' }}>W</Box>

        <Box sx={{ textAlign: 'right', pt: 2, mr: 2, fontSize: '1.2rem' }}>
          <Trans>Discharging Power, maximum</Trans>
        </Box>
        <Box>
          <Text
              data-cy="text-Discharging Power, maximum"
              type="number"
              min={0}
              max={MAX_POWER_W}
              value={maxDischargePower || 0}
              onChange={(e) => setPower('max_discharge_power', e.target.value, false)}
              onBlur={(e) => setPower('max_discharge_power', e.target.value, true)}
            />
        </Box>
        <Box sx={{ pt: 2, ml: 2, fontSize: '1.2rem' }}>W</Box>

        <Box data-cy="box-Self-use power hint" sx={{ gridColumn: '1 / -1', textAlign: 'center', color: 'text.secondary', mb: 1 }}>
          <Trans>Give an estimate of the battery's real power capability. You can also limit the power lower.</Trans>
        </Box>
        </>}

        { socControls && <>
          <Box sx={{ textAlign: 'right', pt: 2, mr: 2, fontSize: '1.2rem' }}>
            <Trans>SOC%, minimum</Trans>
          </Box>
          <Box>
            <Text
                data-cy="text-SOC%, minimum"
                type="number"
                min={0}
                max={100}
                value={minSoc || 0}
                onChange={(e) => this.setConfig('min_soc', parseInt(e.target.value || '0'))}
                onBlur={(e) => this.setConfig('min_soc', parseInt(e.target.value || '0'))}
              />
          </Box>
          <Box sx={{ pt: 2, ml: 2, fontSize: '1.2rem' }}>%</Box>
        </>}

      </Box>

      {beforeTuning}

      <BatteryTuning
        values={this.penaltyValues()}
        onChange={(field, penalty) => this.setConfig(field, penalty)}
        onReset={() => this.resetPenalties()}
      />
    </Box>
  }
}
